import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, MessageSquare, Building2, Loader2, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Name, email, and message are required.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send message. Please try again.";
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 pt-28 pb-24">
        <div className="mx-auto max-w-5xl px-6">
          <span className="inline-flex px-3 py-1 rounded-full bg-neutral-100 text-xs font-medium text-neutral-700">Contact</span>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mt-4">
            We'd love to hear from you
          </h1>
          <p className="mt-3 text-lg text-neutral-600 max-w-2xl">
            Questions about pricing, enterprise, security, or just want to say hi? Send us a note and a real human will reply within one business day.
          </p>

          <div className="mt-12 grid lg:grid-cols-5 gap-10">
            <aside className="lg:col-span-2 space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-neutral-700" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-neutral-900">Email</h3>
                  <a href="mailto:support@transformerus.app" className="text-sm text-neutral-600 hover:text-neutral-900">
                    support@transformerus.app
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-neutral-700" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-neutral-900">Sales & Enterprise</h3>
                  <a href="mailto:sales@transformerus.app" className="text-sm text-neutral-600 hover:text-neutral-900">
                    sales@transformerus.app
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-neutral-700" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-neutral-900">Operated by</h3>
                  <p className="text-sm text-neutral-600">GET Corp LP<br />Registered in the United Kingdom</p>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-2xl bg-neutral-50 border border-neutral-200">
                <p className="text-xs text-neutral-500 leading-relaxed">
                  For data-protection requests under UK / EU GDPR, please email
                  <span className="text-neutral-800"> privacy@transformerus.app</span>.
                </p>
              </div>
            </aside>

            <section className="lg:col-span-3">
              {sent ? (
                <div className="p-8 rounded-3xl border border-emerald-200 bg-emerald-50 text-center">
                  <div className="w-12 h-12 rounded-full bg-white border border-emerald-200 mx-auto flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold text-neutral-900">Message sent</h3>
                  <p className="mt-1 text-sm text-neutral-700">We've received your message and will reply within one business day.</p>
                  <Button onClick={() => setSent(false)} variant="outline" className="mt-5 rounded-full">
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4 p-6 lg:p-8 bg-white border border-neutral-200 rounded-3xl shadow-sm">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm">Name</Label>
                      <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Builder" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm">Email</Label>
                      <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-sm">Subject</Label>
                    <Input id="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Pricing question" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm">Message</Label>
                    <Textarea id="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} placeholder="Tell us what you're working on or what you need help with…" className="mt-1.5 resize-none" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-white">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send message</>
                    )}
                  </Button>
                  <p className="text-[11px] text-neutral-400 text-center">By submitting, you agree to our <a href="/privacy" className="underline">privacy policy</a>.</p>
                </form>
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
